/**
 * Entity surface for the global frontend shell.
 *
 * `entityHooks` is populated once per realm by
 * `initRuntimeEntityHooksFromConnectorConfig()`, which is the one and
 * only caller of `createEntityHooks` in this module. It runs from
 * `AppWithLayout`'s `RuntimeBootGate` — i.e. inside the React component
 * that wraps a realm's runtime app. Launcher / settings / callback /
 * builder routes never mount that gate, so they never load entity
 * bundles and never see an `entityHooks` instance.
 *
 * Call `publishEntityGlobals()` after init so Module Federation remotes
 * can read `window.__DATA_PLANE_ENTITIES__`.
 *
 * There is no dev-catalog or "load all entities" fallback. Standalone
 * module development uses `data-plane-module-harness`, which constructs
 * its own entity-hook surface from a hand-picked tuple of packages.
 */
import {
  discoverAndPersistEntityExtras,
  entityHooks,
  getActiveEntityPackages,
  getEntityHooks,
  getEntityPackageByHookKey,
  getFailedEntityPackages,
  initRuntimeEntityHooksFromConnectorConfig,
  isRuntimeEntityHooksInitialized,
} from './hooks';

export {
  discoverAndPersistEntityExtras,
  entityHooks,
  getActiveEntityPackages,
  getEntityHooks,
  getEntityPackageByHookKey,
  getFailedEntityPackages,
  initRuntimeEntityHooksFromConnectorConfig,
  isRuntimeEntityHooksInitialized,
};
export type { DiscoverResult } from './hooks';
export type { State, EntityStates, FocusItem, PinnedItem } from './hooks';

export function publishEntityGlobals(): void {
  if (typeof window === 'undefined') return;
  const hooks = getEntityHooks();
  // Remotes read the namespaced `entityHooks.<entity>.useLive()` /
  // `.useMutation()` proxy off this global. There is no flat-named alias
  // surface — consumers use the namespaced bundles directly.
  (window as any).__DATA_PLANE_ENTITIES__ = {
    entityHooks: hooks,
  };
}

export { getActiveEntityPackages as ENTITY_PACKAGES };
